// The catalog admin surfaces' shared vocabulary: which lookup domains exist,
// what to call them, and the two field bounds the forms validate against.
//
// It lives in `lib/` for the reason that tier exists — it is named from more
// than one place and depends on nothing above it: the browser-seam contracts
// (`@/api/contracts/catalog-admin`) validate against it, the server functions
// dispatch on it, and the panels paint it. See `client/docs/architecture.md`,
// "The dependency rule", and the same split `@/lib/admin-tabs` makes for the
// admin section's tab vocabulary.
//
// Tiers are not a lookup kind and are deliberately absent from the kind tuple
// below. They carry a different shape (`value`, `isEi`) and a different verb set
// — no delete, ADR-0005 §8 — so folding them into `LOOKUP_KINDS` would buy one
// shared list at the price of a delete affordance that must not exist. They do
// join the *panes* tuple further down, which is a different thing: one screen
// each, not one component each.

/**
 * The three lookup domains, in the order the Item Catalog tab lists them.
 *
 * Manufacturer first because it is the one with a locked row and the only one
 * items carry a NOT NULL FK to (ADR-0005 §13); material and finish follow the
 * order the item form reads them in.
 *
 * The strings are the *wire's* own kind names — Python echoes exactly these
 * back on `LookupListOutput.kind` (`server/app/api/contracts/catalog_admin.py`)
 * — so a rename here is a wire change, not a relabel. The labels below are
 * what the screen says.
 */
export const LOOKUP_KINDS = ["manufacturer", "material", "finish"] as const;

export type LookupKind = (typeof LOOKUP_KINDS)[number];

/**
 * One row of a lookup, as every layer that handles one sees it.
 *
 * Here rather than beside the select that draws it, for the reason the header
 * above gives: the hooks that create and rename a lookup answer in this shape,
 * and a hook importing a type from a component is the dependency rule pointing
 * the wrong way (`client/docs/architecture.md`, "The dependency rule"). The
 * select re-exports it, so a caller that only knows the component still has it
 * to hand.
 */
export interface LookupOption {
  id: number;
  name: string;
  /** The server's answer to "may this row be edited at all" — never re-derived
   * on the client (`lookupWire`). */
  isLocked: boolean;
}

export function isLookupKind(value: unknown): value is LookupKind {
  return typeof value === "string" && (LOOKUP_KINDS as readonly string[]).includes(value);
}

/**
 * What each domain is called on screen, singular and plural.
 *
 * Both forms, because the panel needs each in a place the other will not do:
 * the section heading counts them ("Manufacturers") and every message names one
 * ("Enter a manufacturer name", "Delete this material?"). Deriving the plural
 * by adding an "s" would work for all three of today's kinds and quietly break
 * on the first one it does not.
 */
export const LOOKUP_KIND_LABELS: Record<LookupKind, { singular: string; plural: string }> = {
  manufacturer: { singular: "manufacturer", plural: "Manufacturers" },
  material: { singular: "material", plural: "Materials" },
  finish: { singular: "finish", plural: "Finishes" },
};

/**
 * The Item Catalog tab's sub-tabs, in bar order — one screen per domain.
 *
 * Derived from `LOOKUP_KINDS` rather than re-listed, so a fourth lookup domain
 * arrives with its own sub-tab and cannot be added to the vocabulary while
 * staying unreachable in the UI. Tiers are appended by hand because they are
 * not a lookup kind (see the note at the top of this file) but *are* a screen
 * in the same tab: an admin tidying the catalog meets all four in one place,
 * and only ever works one at a time — which is what makes them sub-tabs rather
 * than four stacked sections.
 *
 * These strings are URL surface (`?tab=item-catalog&catalog=<pane>`), the same
 * way the admin tab slugs are — see `@/lib/admin-tabs`. For the three lookup
 * panes the slug is the wire's own kind name, which keeps the derivation above
 * honest: one name for one domain, in the URL and on the wire.
 */
export const CATALOG_PANES = [...LOOKUP_KINDS, "tier"] as const;

export type CatalogPane = (typeof CATALOG_PANES)[number];

/**
 * The sub-tab label for each pane.
 *
 * The three lookup labels are read from `LOOKUP_KIND_LABELS` rather than
 * retyped: the bar and the pane heading below it name the same domain, and two
 * copies of "Finishes" is exactly the pair that drifts. Still an exhaustive
 * `Record`, so a new pane cannot ship unlabelled.
 */
export const CATALOG_PANE_LABELS: Record<CatalogPane, string> = {
  manufacturer: LOOKUP_KIND_LABELS.manufacturer.plural,
  material: LOOKUP_KIND_LABELS.material.plural,
  finish: LOOKUP_KIND_LABELS.finish.plural,
  tier: "Tiers",
};

/**
 * The pane a bare `?tab=item-catalog` opens — and where an unrecognized
 * `?catalog=` lands, the same fallback the admin tab bar makes for `?tab=`.
 *
 * Manufacturers, because it is the domain with the locked row and the only one
 * items carry a NOT NULL FK to (ADR-0005 §13). Putting another pane in front of
 * it would quietly change where every existing Item Catalog link opens.
 */
export const DEFAULT_CATALOG_PANE: CatalogPane = CATALOG_PANES[0];

/**
 * The width of every `name` column in the catalog's lookup and category tables.
 *
 * Mirrored from `server/app/use_cases/catalog/lookup_name.py`, which refuses
 * rather than truncates; `catalog-admin-drift.unit.test.ts` is the pin. The form
 * validates against it so an over-long name is a field error the typist can see
 * rather than a round trip that comes back tagged.
 */
export const NAME_MAX_LENGTH = 255;

/**
 * How long a name is, counted the way Python counts it.
 *
 * `String.prototype.length` counts UTF-16 code *units*, and Python's `len()` on
 * a `str` counts code *points* — so an astral character (an emoji, most CJK
 * extension characters) is 2 to JS and 1 to Python. Using `.length` made the BFF
 * refuse names `normalized_name` would have accepted, which is the BFF deciding
 * a rule the server owns.
 *
 * Code points, not grapheme clusters: a family emoji is several code points to
 * Python too, and matching Python exactly is the whole requirement. The spread
 * iterates by code point, which is what makes this different from `.length`.
 */
export const nameLength = (name: string): number => [...name].length;

/**
 * The range `catalog_tiers.value` can hold — signed 32-bit.
 *
 * Mirrored from `server/app/use_cases/catalog/tier_value.py`, which raises a
 * tagged `invalid-input:tier-value` outside it. Same reason as above: the bound
 * is deliberately off both wire contracts (it is a constraint, not a shape), so
 * this copy plus the drift test is how the form can refuse the number before
 * spending a request on it.
 */
export const TIER_VALUE_MIN = -2147483648;
export const TIER_VALUE_MAX = 2147483647;

/** What references one manufacturer — #803's usage read, as the delete control
 *  reads it. */
export interface ManufacturerUsageCounts {
  items: number;
  selections: number;
  specifications: number;
}

/**
 * Why a manufacturer cannot be deleted, or `null` when nothing references it.
 *
 * A copy of `lookup_kind.raise_manufacturer_in_use`'s sentence, character for
 * character, because the usage read answers with counts and the server sends
 * the sentence only on a refused delete — and the editor's delete control has
 * to say it before any delete is tried (#858). The unit tests on both sides pin
 * the same literal sentences, so the copies cannot drift unseen. Each noun is
 * singular at exactly 1 (#861 D4 b) on both sides; a change to the wording
 * edits both.
 */
export function manufacturerInUseMessage(
  name: string,
  { items, selections, specifications }: ManufacturerUsageCounts,
): string | null {
  if (items === 0 && selections === 0 && specifications === 0) return null;
  return `"${name}" cannot be deleted because the manufacturer is used in ${counted(items, "item")} and ${counted(selections, "selection")} across ${counted(specifications, "specification")}.`;
}

/** `1 item`, `0 items`, `3 items`: the noun singular at exactly 1 (#861 D4 b). */
function counted(count: number, noun: string): string {
  return count === 1 ? `${count} ${noun}` : `${count} ${noun}s`;
}
